import type { EditorView } from '@codemirror/view';
import { useEffect, useRef, useState } from 'react';
import {
  createGlobalTag,
  createListTag,
  findOrCreateList,
  toggleDone,
  updateGlobalTag,
  updateListTag,
} from '../db/dexie';
import type { Item, List } from '../db/types';
import { useAttachments, useGlobalTags, useListTags } from '../hooks/useData';
import { useTypedText } from '../hooks/useTypedText';
import { formatClock, isOverdue } from '../lib/dates';
import { goToLine } from '../lib/markdownCommands';
import { statsOf } from '../lib/outline';
import { Checkbox } from './Checkbox';
import { EditorToolbar } from './EditorToolbar';
import { Lightbox } from './Lightbox';
import { ListDialog } from './ListDialog';
import { MarkdownEditor } from './MarkdownEditor';
import { OutlinePanel } from './OutlinePanel';
import { PropertiesPanel } from './PropertiesPanel';
import { TagDialog } from './TagDialog';
import './ItemEditor.css';

/** Which tag is being created or edited, and in which scope. */
interface TagDialogState {
  scope: 'list' | 'global';
  /** Present when editing an existing tag. */
  id?: string;
  name?: string;
  color?: string | null;
}

/** Panel widths, and the narrowest body worth keeping them open for. */
const OUTLINE_W = 210;
const PROPERTIES_W = 260;
const MIN_BODY = 460;

interface Props {
  item: Item;
  lists: List[];
  allItems: Item[];
  /** Breadcrumb text — where the item was opened from. */
  crumb: string;
  /** "Draft" until the item has been committed to the database. */
  draft?: boolean;
  /**
   * Where edits go. The editor does not own persistence — a draft is held in
   * memory until it has content worth keeping, and only the caller knows that.
   */
  onPatch: (patch: Partial<Item>) => void;
  onDelete: () => void;
  onOpenItem: (item: Item) => void;
  onBack?: () => void;
}

/**
 * The note and task editor.
 *
 * Toolbar across the top, outline on the left, properties on the right, status
 * bar along the bottom — with the body holding the centre. Both side panels
 * close, because the body's measure matters more than either of them.
 */
export function ItemEditor({
  item,
  lists,
  allItems,
  crumb,
  draft = false,
  onPatch,
  onDelete,
  onOpenItem,
  onBack,
}: Props) {
  const [view, setView] = useState<EditorView | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  /** Which small window is open, if any. */
  const [tagDialog, setTagDialog] = useState<TagDialogState | null>(null);
  const [listDialog, setListDialog] = useState(false);
  const listTags = useListTags();
  const globalTags = useGlobalTags();
  // Properties earns its place by default; the outline only pays for itself in
  // a long note, so it starts closed and is one click away.
  const [panels, setPanels] = useState({ outline: false, properties: true });
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const attachments = useAttachments(item.id);
  const isNote = item.kind === 'note';

  const set = (patch: Partial<Item>) => {
    onPatch(patch);
    setSavedAt(new Date().toISOString());
  };

  /**
   * The body's measure is the thing worth protecting — a side panel is never
   * worth squeezing the text below readable. Panels are shed only when what
   * remains would be genuinely cramped, and otherwise left to the toolbar
   * toggles; auto-closing a panel someone just opened is worse than narrow.
   */
  const frame = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = frame.current;
    if (!el) return;

    const measure = () => {
      const pane = el.getBoundingClientRect().width;
      if (pane === 0) return; // not laid out yet
      setPanels((current) => {
        // Outline goes first: it is the panel you consult, not the one you edit in.
        let outline = current.outline;
        let properties = current.properties;
        if (outline && pane - OUTLINE_W - (properties ? PROPERTIES_W : 0) < MIN_BODY) outline = false;
        if (properties && pane - (outline ? OUTLINE_W : 0) - PROPERTIES_W < MIN_BODY) properties = false;
        return outline === current.outline && properties === current.properties
          ? current
          : { outline, properties };
      });
    };

    measure();
    // The observer catches pane changes that are not window resizes (a future
    // draggable splitter); the window listener is what actually fires in
    // WebViews and any context where frames are not being composited.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [panels.outline, panels.properties]);

  const stats = statsOf(item.body);
  const saveLabel = draft ? 'Draft' : savedAt ? `Saved ${formatClock(savedAt)}` : 'Saved';

  return (
    <section className="editor" ref={frame} aria-label={item.title || 'Untitled'}>
      <EditorToolbar
        view={view}
        title={crumb}
        saveLabel={saveLabel}
        onBack={onBack}
        outlineOpen={panels.outline}
        propertiesOpen={panels.properties}
        onToggleOutline={() => setPanels((p) => ({ ...p, outline: !p.outline }))}
        onToggleProperties={() => setPanels((p) => ({ ...p, properties: !p.properties }))}
      />

      <div className="editor__frame">
        {panels.outline && (
          <OutlinePanel
            item={item}
            allItems={allItems}
            onGoToLine={(line) => view && goToLine(view, line)}
            onOpenItem={onOpenItem}
            onClose={() => setPanels((p) => ({ ...p, outline: false }))}
          />
        )}

        <div className="editor__main">
          <div className="editor__scroll">
            <div className="editor__column">
              <div className="editor__titleRow">
                {!isNote && (
                  <Checkbox
                    kind="todo"
                    size={20}
                    done={item.done}
                    overdue={isOverdue(item.due_at, item.done)}
                    onToggle={() => void toggleDone(item)}
                  />
                )}
                <TitleInput
                  itemId={item.id}
                  value={item.title}
                  done={!isNote && item.done}
                  onChange={(title) => set({ title })}
                />
              </div>

              <div className="editor__body">
                <MarkdownEditor
                  docKey={item.id}
                  value={item.body}
                  onReady={setView}
                  onChange={(body) => set({ body })}
                  placeholder={isNote ? 'Start writing…' : 'Notes on this task…'}
                />
              </div>
            </div>
          </div>

          <div className="statusbar">
            <button
              type="button"
              className="statusbar__action"
              onClick={() => set({ kind: isNote ? 'todo' : 'note' })}
            >
              {isNote ? 'Convert to task' : 'Convert to note'}
            </button>
            <button type="button" className="statusbar__action" onClick={onDelete}>
              Delete
            </button>
            <span className="spacer" />
            <span className="statusbar__stat">{stats.words} words</span>
            <span className="statusbar__stat">{stats.characters} characters</span>
            <span className="statusbar__stat statusbar__stat--format">Markdown</span>
          </div>
        </div>

        {panels.properties && (
          <PropertiesPanel
            item={item}
            lists={lists}
            listTags={listTags}
            globalTags={globalTags}
            attachments={attachments}
            onPatch={set}
            onMoveList={(listId) => onPatch({ list_id: listId, list_tag_ids: [] })}
            onNewList={() => setListDialog(true)}
            onCreateListTag={(name) => setTagDialog({ scope: 'list', name })}
            onEditListTag={(tag) => setTagDialog({ scope: 'list', ...tag })}
            onCreateGlobalTag={(name) => setTagDialog({ scope: 'global', name })}
            onEditGlobalTag={(tag) => setTagDialog({ scope: 'global', ...tag })}
            onOpenImage={setLightbox}
            onClose={() => setPanels((p) => ({ ...p, properties: false }))}
          />
        )}
      </div>

      {lightbox !== null && attachments.length > 0 && (
        <Lightbox
          attachments={attachments}
          index={Math.min(lightbox, attachments.length - 1)}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}

      {tagDialog && (
        <TagDialog
          scope={tagDialog.scope}
          listName={lists.find((l) => l.id === item.list_id)?.name}
          initial={tagDialog}
          editing={tagDialog.id !== undefined}
          onClose={() => setTagDialog(null)}
          onSave={async (draft) => {
            // Renaming only touches the tag row — items reference tags by id,
            // so a rename is invisible to every note carrying it.
            if (tagDialog.id) {
              if (tagDialog.scope === 'list') await updateListTag(tagDialog.id, draft);
              else await updateGlobalTag(tagDialog.id, draft);
            } else if (tagDialog.scope === 'list') {
              const tag = await createListTag({ ...draft, list_id: item.list_id });
              set({ list_tag_ids: [...item.list_tag_ids, tag.id] });
            } else {
              const tag = await createGlobalTag(draft);
              set({ global_tag_ids: [...item.global_tag_ids, tag.id] });
            }
            setTagDialog(null);
          }}
        />
      )}

      {listDialog && (
        <ListDialog
          onClose={() => setListDialog(false)}
          onSave={async ({ name, color }) => {
            const list = await findOrCreateList(name, color);
            onPatch({ list_id: list.id, list_tag_ids: [] });
            setListDialog(false);
          }}
        />
      )}
    </section>
  );
}

/** Auto-growing title. A long title wraps rather than scrolling sideways. */
function TitleInput({
  itemId,
  value,
  done,
  onChange,
}: {
  itemId: string;
  value: string;
  done: boolean;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Same round trip as the body: what has been typed outranks what the
  // database has managed to hand back so far.
  const [text, typed] = useTypedText(value, itemId);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  return (
    <textarea
      ref={ref}
      className={`editor__title${done ? ' is-done' : ''}`}
      value={text}
      rows={1}
      placeholder="New note title"
      aria-label="Title"
      onChange={(e) => {
        const next = e.target.value.replace(/\n/g, '');
        typed(next);
        onChange(next);
      }}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
    />
  );
}
